<template lang='pug'>
  div
    bionic-metrics-page-loading(v-if='loading')
    v-container.test-status-container(fluid grid-list-md v-if='!loading').pt-0
      v-row(wrap dense)
        v-col(v-for='testSuite in testSuites' :key='testSuite.guid' cols='12' :lg='lgCols' :id='getId(testSuite.name)')
          bionic-metrics-test-status-card(
            :dashboardGuid='dashboardGuid'
            :listView='listView'
            :testSuite='testSuite'
            :suiteStatus='testSuiteStatus[testSuite.guid]'
            :status='status'
            :previewLog='logs[testSuite.runGuid]'
            @starting='onStarting'
            @cancelled='onCancelled')
</template>

<script>
import _ from 'lodash';
import {mapGetters} from 'vuex';
import UtilMixin from '../mixins/util.mixin';
import Dashboard from '../api/dashboard';
import Notifications from '../services/notification.service';
import Messages from '../services/message.service';
import BionicMetricsPageLoading from './PageLoading';
import BionicMetricsTestStatusCard from './TestStatusCard';

export default {
  name: 'BionicMetricsTestStatus',
  mixins: [UtilMixin],
  components: {
    BionicMetricsTestStatusCard,
    BionicMetricsPageLoading
  },
  props: {
    testSuites: {
      type: Array
    },
    listView: {
      type: Boolean,
      default: false
    },
    dashboardGuid: {
      type: String,
      required: true
    }
  },
  async mounted() {
    await this.load();
  },
  async beforeDestroy() {
    await Messages.unsubscribe(this.channels, this.messageListener, this.messageKey);
  },
  computed: {
    lgCols() {
      if (!this.listView) {
        return 6;
      }
      return 12;
    },
    ...mapGetters({
      isAssumedAccount: 'isAssumedAccount',
      user: 'getUser',
      branch: 'getGitBranch'
    })
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        this.testSuiteStatus = await Dashboard.testSuiteStatus(this.dashboardGuid);
        const {map, runGuids} = await Dashboard.status(this.dashboardGuid);
        this.status = map;
        this.runGuids = runGuids;

        // Setup message listeners
        this.messageListener = {message: this.onMessage};
        this.channels = this.runGuids.concat(this.dashboardGuid); // Listen on all runGuids and dashboardGuid (for newRunGuid event)
        await Messages.subscribe(this.channels, this.messageListener, this.messageKey);
      } catch (e) {
        console.error(`Error Loading Dashboard: ${e.message}`);
        Notifications.errorToast('Error Loading Dashboard', {duration: 30000, retry: this.load});
      }
      this.loading = false;
    },
    async addRunGuidListener(runGuid) {
      if (!runGuid) return;
      this.channels.push(runGuid);
      await Messages.addChannel(runGuid);
    },
    async removeRunGuidListener(runGuid) {
      if (!runGuid) return;
      this.channels = this.channels.filter((c) => c !== runGuid);
      await Messages.removeChannel(runGuid);
    },
    updateStatus(message) {
      const key = message.testSuiteGuid;
      const theStatus = message.status;
      const {source} = message;
      let runningCount = _.get(this.testSuiteStatus, `${key}.isRunning`, 0);
      if (message.isRunning) runningCount++;
      else if (runningCount) runningCount--;
      if (message.isRunning === false) this.$emit('refresh'); // update summary counts

      if (this.testSuiteStatus && !(key in this.testSuiteStatus)) this.$set(this.testSuiteStatus, key, {});
      this.$set(this.testSuiteStatus[key], 'status', theStatus);
      this.$set(this.testSuiteStatus[key], 'isRunning', runningCount);
      this.$set(this.testSuiteStatus[key], 'statusUpdatedAt', new Date());
      this.$set(this.testSuiteStatus[key], 'isRunningUpdatedAt', new Date());
      this.$set(this.testSuiteStatus[key], 'isStarting', !!message.isStarting);
      console.log(`Status: ${key} - ${theStatus} - runningCount: ${runningCount} - from ${source},`);
    },
    updateTestStatus(message) {
      const {runGuid, testGuid, testIndex, status, isRunning} = message;
      const key = `${runGuid}-${testGuid}-${testIndex}`;
      const theStatus = status;
      const update = this.status[key] || {};
      update.status = theStatus;
      update.isRunning = !!isRunning;
      update.statusUpdatedAt = new Date();
      update.isRunningUpdatedAt = new Date();
      this.$set(this.status, key, update);
    },
    getTestName(runGuid, index) {
      if (!runGuid || !_.isNumber(index)) return '';
      const testSuite = this.testSuites.find((ts) => ts.runGuid === runGuid);
      return `${_.get(testSuite, `tests.${index}.name`)}: `;
    },
    updateTargetVersion(message) {
      const {testSuiteGuid, targetVersion} = message;
      if (!testSuiteGuid) return;
      if (testSuiteGuid in this.testSuiteStatus) {
        this.$set(this.testSuiteStatus[testSuiteGuid], 'targetVersion', targetVersion);
      } else {
        console.log(`testSuiteStatus did not contain ${testSuiteGuid}`);
      }
    },
    showLog(runGuid, message) {
      const stripped = message.log.slice(message.log.indexOf(': ') + 2);
      const name = this.getTestName(runGuid, message.index);
      this.$set(this.logs, runGuid, `${name}${stripped}`);
    },
    onMessage(data) {
      const type = _.get(data, 'message.type');
      if (this.debug && type) console.log(`TestStatus.vue "${type}" ${JSON.stringify(data)}`);
      if (type === 'newRunGuid') return this.updateRunGuid(data.message);
      if (type === 'status') return this.updateStatus(data.message);
      if (type === 'testStatus') return this.updateTestStatus(data.message);
      if (type === 'targetVersion') return this.updateTargetVersion(data.message);
      if (type === 'log') return this.showLog(data.channel, data.message);
    },
    async onStart(testSuite, runGuid, local) {
      if (local) Notifications.toast(`Starting ${testSuite.name} on local machine`);
      else Notifications.toast(`Starting ${testSuite.name}`);
      const log = `${new Date()}: Starting. Waiting for agent.`;
      await this.updateStatus({testSuiteGuid: testSuite.guid, isStarting: true});
      await this.showLog(runGuid, {log, runGuid});
    },
    async onStarting({testSuiteGuid}) {
      // this is necessary because there's a gap between when the HTTP request finished
      // and the websocket hits onStart() above that the button will re-enable for a second
      await this.updateStatus({testSuiteGuid, isStarting: true});
    },
    async onCancelled(testSuite) {
      this.$emit('newRunGuid', {testSuiteGuid: testSuite.guid, runGuid: null});
      this.$delete(this.testSuiteStatus, testSuite.guid);
      await this.removeRunGuidListener(testSuite.runGuid);
      Notifications.toast(`Tests cancelled for ${testSuite.name}`);
    },
    async updateRunGuid(message) {
      const {gitMode, gitBranch, runGuid, baseUrl, dashboardGuid, testSuiteGuid, local} = message;
      const newRunGuid = runGuid;

      // Make sure it's for us first
      if (dashboardGuid !== this.dashboardGuid) {
        console.log('Not for this dashboard so ignoring');
        return;
      }
      // Make sure it's for our branch
      if (gitMode && gitBranch !== this.branch) {
        console.log('gitMode enabled and not our current branch so ignoring new runGuid');
        return;
      }

      // The the suite we care about
      const testSuite = this.testSuites.find((ts) => ts.guid === testSuiteGuid);

      // Update listeners
      await this.removeRunGuidListener(testSuite.runGuid);
      await this.addRunGuidListener(newRunGuid);

      // Tell parent about it
      this.$emit('newRunGuid', {testSuiteGuid, runGuid: newRunGuid});
      this.updateTargetVersion({testSuiteGuid: testSuite.guid, targetVersion: null});

      // Update our status to be correct
      if (!(testSuite.guid in this.testSuiteStatus)) this.$set(this.testSuiteStatus, testSuite.guid, {});
      if (baseUrl) this.$set(this.testSuiteStatus[testSuite.guid], 'baseUrl', baseUrl);

      // If starting or cancelling
      if (newRunGuid) {
        await this.onStart(testSuite, newRunGuid, local);
      } else {
        await this.onCancelled(testSuite);
      }
    },
    getId(name) {
      return _.kebabCase(name);
    }
  },
  data() {
    return {
      messageKey: 'test-status',
      messageListener: null,
      loading: false,
      runGuids: [],
      channels: [],
      status: {},
      testSuiteStatus: {},
      logs: {},
      debug: true
    };
  }
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.test-status-container {
  margin-top: 0;
}
</style>
